import {
  buildEndNodeId,
  type InsertDraftStepBetweenRequest,
  type ProcessDraftStep
} from '@bliks-io/contracts';
import { positionAfterStep } from '@bliks-io/ui';
import { resolveCurrentLaneKey } from '../../graph/resolveLane';
import { addSourceToNodeInputs } from '../../../../../../api/mutations/helpers/draftGraphOperations';
import {
  appendPlaceholderPosition,
  buildPlaceholderStep,
  envelopePlaceholderReconcilers,
  replaceGeneratedPosition
} from '../../../../../../api/mutations/helpers/placeholderStep';
import { mintTempDraftStepId } from '../../../../../../api/mutations/useProcessDraft/shared';
import { insertDraftStepBetweenApi } from '../../../../../../api/persist/draft';
import { findStepByStableId } from '../shared';
import { ensureNotTerminal } from '../successorInvariant';
import type { EdgeActionDeps } from '../types';
import { ruleIdsForConnect } from './shared';

export async function insertBetweenStepAndEnd(
  sourceStepId: string,
  ruleId: string | undefined,
  position: { x: number; y: number },
  deps: EdgeActionDeps
): Promise<void> {
  const { processId } = deps;
  const allSteps = deps.getCachedSteps();
  const step = findStepByStableId(allSteps, sourceStepId);
  if (!step) return;
  const laneHint = resolveCurrentLaneKey(step, allSteps, deps.getAssigneeLookup?.());
  const ruleIdsForEdge = ruleIdsForConnect(step, ruleId);
  const mInputs =
    addSourceToNodeInputs(
      { inputs: [] } as unknown as ProcessDraftStep,
      sourceStepId,
      ruleIdsForEdge
    ).inputs ?? [];
  const terminalTask = {
    taskDescription: '',
    output: '',
    successors: { successorStatus: 'NONE' }
  };

  // M's end marker keeps the old marker's saved position: the synthetic
  // `end-<sourceStepId>` id is replaced by `end-<M>`, and the canvas default
  // (one column right of M) would make it jump. Without a saved position it
  // takes that default here, so the canvas has nothing left to seed in a
  // separate layout write.
  const oldEndNodeId = buildEndNodeId(sourceStepId, ruleId);
  const oldEndPos = deps.getCachedGeneratedPositions().find((g) => g.stepId === oldEndNodeId);
  const endPosition = oldEndPos ? { x: oldEndPos.x, y: oldEndPos.y } : positionAfterStep(position);

  // Optimistic-only: a terminal insert VALIDATES server-side that S still
  // carries the ended marker (the endpoint transfers it onto M itself), so
  // persisting a clear here would 400 the insert. The staged patch keeps the
  // canvas from rendering S's end marker and the S→M edge simultaneously.
  ensureNotTerminal(sourceStepId, ruleId, deps);

  const performInsert = async (): Promise<string> => {
    const tempId = mintTempDraftStepId();
    const reconcilers = envelopePlaceholderReconcilers(tempId);
    const body: InsertDraftStepBetweenRequest = {
      source: {
        kind: 'node',
        stableStepId: sourceStepId,
        ...(ruleId ? { ruleId } : {})
      },
      // [] = terminal insert: M takes over the "ends here" marker and S's
      // marker is cleared in the same transaction.
      targetStableStepIds: [],
      position,
      ...(laneHint ? { laneKey: laneHint } : {}),
      endPosition
    };

    const envelope = await deps.runTransaction({
      optimistic: (prev) => ({
        ...prev,
        draft: { ...prev.draft, updatedAt: new Date() },
        steps: [
          ...prev.steps,
          buildPlaceholderStep({
            tempId,
            processId,
            draftId: prev.draft._id,
            inputs: mInputs,
            task: terminalTask as ProcessDraftStep['task'],
            laneKey: laneHint
          })
        ]
      }),
      optimisticLayout: (prev) => {
        const withPosition = appendPlaceholderPosition(prev, tempId, position);
        // Seed the synthetic end-node's generated position under the temp id
        // so that — during the optimistic phase — the end handle renders at
        // the preserved spot rather than the sentinel.
        return replaceGeneratedPosition(withPosition, {
          nodeId: buildEndNodeId(tempId),
          ...endPosition
        });
      },
      persist: () => insertDraftStepBetweenApi(processId, body),
      reconcile: reconcilers.reconcile,
      reconcileLayout: (prev, env) => {
        const layout = reconcilers.reconcileLayout(prev, env);
        // Also swap the temp-keyed synthetic-end generated position to the
        // real stableStepId's end-node so the end handle keeps rendering at
        // the preserved spot across the placeholder→real transition.
        const tempEndId = buildEndNodeId(tempId);
        const realEndId = buildEndNodeId(String(env.createdNodeIds[0]));
        const idx = layout.generatedNodePositions.findIndex((g) => g.nodeId === tempEndId);
        if (idx === -1) return layout;
        const swapped = [...layout.generatedNodePositions];
        swapped[idx] = { ...swapped[idx], nodeId: realEndId };
        return { ...layout, generatedNodePositions: swapped };
      }
    });

    return String(envelope.createdNodeIds[0]);
  };

  await performInsert();
}
